import { selectAllCadModels } from "@/store/cad/cad-selectors";
import { startIpxOrderWizard } from "@/store/modes/ipx-order-mode-thunks";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FaroButton, FaroText, grey } from "@faro-lotv/flat-ui";
import { Stack } from "@mui/material";
import type { JSX } from "react";
import { useCanOrderFromIpx } from "./use-can-order-from-ipx";

type GetAsBuiltEntryProps = {
  /** Where the entry is rendered in the 3D Model tab: the intro on top without models, the button at the bottom with models */
  position: "top" | "bottom";
};

/** @returns the Get As-Built button of the 3D Model tab, explaining the IPX service while the project has no models */
export function GetAsBuiltEntry({
  position,
}: GetAsBuiltEntryProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const hasModels = useAppSelector(
    (state) => selectAllCadModels(state).length > 0,
  );
  const canOrder = useCanOrderFromIpx();
  if (!canOrder || hasModels !== (position === "bottom")) return null;

  const button = (
    <FaroButton
      variant="secondary"
      aria-label="get as-built"
      onClick={() => dispatch(startIpxOrderWizard("modelsTab"))}
      sx={{ width: "100%" }}
    >
      Get As-Built
    </FaroButton>
  );

  if (hasModels) {
    return <Stack sx={{ mx: 2, mt: 1 }}>{button}</Stack>;
  }

  return (
    <Stack
      sx={{
        gap: 1,
        p: 2,
        mb: 2,
        mr: 2,
        borderRadius: 1,
        border: `1px solid ${grey[200]}`,
        backgroundColor: grey[50],
      }}
    >
      <FaroText variant="heading14">No 3D model yet?</FaroText>
      <FaroText variant="bodyS" color={grey[700]}>
        Get as-built floor plans and BIM models from your point cloud. Select
        the region in Sphere XG and order from Integrated Projects (IPX), the
        results are added to this project once processing is completed.
      </FaroText>
      {button}
    </Stack>
  );
}
